const React = require('react')
const { useState } = require('react')
const { useHistory } = require('react-router-dom')
const { UncontrolledTooltip, Table } = require('reactstrap')
const { useFormatBytes, useFormatTimestampShort, useFormatDateTime, useResponsive, BREAKPOINTS } = require('@hooks')
const { useNetworkConfig } = require('@contexts')

const { CopyButton } = require('@components/copyButton')

require('@styles/components/transactions.css')

const InternalTransactionsTable = props => {
    const { data, addressDisabled } = props
    const history = useHistory()
    const { network } = useNetworkConfig()
    const formatBytes = useFormatBytes()
    const formatTimestamp = useFormatTimestampShort()
    const formatDateTime = useFormatDateTime()
    const [showAge, setShowAge] = useState(true)

    const isMobile = useResponsive(BREAKPOINTS.large)

    return (
        <Table responsive style={isMobile ? { display: 'block', overflowX: 'auto', whiteSpace: 'nowrap' } : {}}>
            <thead>
                <tr>
                    <th>Block</th>
                    <th>
                        <span
                            id='time-format-tooltip'
                            className={`${network.name}-text-color cursor-pointer`}
                            onClick={() => setShowAge(!showAge)}
                        >
                            {showAge ? 'Age' : 'Date Time (UTC)'}
                        </span>
                        <UncontrolledTooltip placement='top' target='time-format-tooltip'>
                            {showAge ? 'Click to show Datetime Format' : 'Click to show Age Format'}
                        </UncontrolledTooltip>
                    </th>
                    <th>Parent Transaction Hash</th>
                    <th>Type</th>
                    <th>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <span>Method</span>
                            <div
                                id='method-tooltip'
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <i
                                    className='fas fa-circle-question'
                                    style={{
                                        fontSize: '14px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                            </div>
                            <UncontrolledTooltip placement='top' target='method-tooltip'>
                                Function executed based on decoded input data. For unidentified functions, method ID is
                                displayed instead
                            </UncontrolledTooltip>
                        </div>
                    </th>
                    <th>From</th>
                    <th>To</th>
                    <th>Amount</th>
                </tr>
            </thead>
            <tbody>
                {data && data.length > 0 && data[0].transactions.length === 0 ? (
                    <tr>
                        <td colSpan='9' style={{ textAlign: 'center', padding: '40px 0', height: '400px' }}>
                            <div
                                style={{
                                    width: '72px',
                                    height: '72px',
                                    backgroundColor: 'rgba(255, 193, 7, 0.15)',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 12px'
                                }}
                            >
                                <i
                                    className='fas fa-inbox'
                                    style={{
                                        fontSize: '24px',
                                        color: 'var(--grey-color)'
                                    }}
                                />
                            </div>
                            <div style={{ fontWeight: 'bold', fontSize: '16px' }}>There are no matching entries</div>
                            <div style={{ color: 'var(--grey-color)', marginTop: '4px' }}>Please try again later</div>
                        </td>
                    </tr>
                ) : (
                    <>
                        {data?.map(block => {
                            return (
                                <>
                                    {block.transactions.map((transaction, index) => {
                                        return (
                                            <tr key={`${block.blockNumber}-${transaction.hash}`}>
                                                <td>
                                                    {index === 0 && (
                                                        <span
                                                            className={`${network.name}-text-color cursor-pointer`}
                                                            onClick={() =>
                                                                history.push(
                                                                    `/${network.name}/block/${block.blockNumber}`
                                                                )
                                                            }
                                                        >
                                                            {block.blockNumber}
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    {index === 0 &&
                                                        (showAge ? (
                                                            <>
                                                                <span id={'age-' + block.blockNumber}>
                                                                    {formatTimestamp(block.blockTimestamp)}
                                                                </span>
                                                                <UncontrolledTooltip
                                                                    placement='top'
                                                                    target={`age-${block.blockNumber}`}
                                                                >
                                                                    {formatDateTime(block.blockTimestamp)}
                                                                </UncontrolledTooltip>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span id={'datetime-' + block.blockNumber}>
                                                                    {formatDateTime(block.blockTimestamp)}
                                                                </span>
                                                                <UncontrolledTooltip
                                                                    placement='top'
                                                                    target={`datetime-${block.blockNumber}`}
                                                                >
                                                                    {formatTimestamp(block.blockTimestamp)}
                                                                </UncontrolledTooltip>
                                                            </>
                                                        ))}
                                                </td>
                                                <td>
                                                    {transaction.status ? (
                                                        <div className='tx-status-indicator'>
                                                            <i
                                                                className='fas fa-check'
                                                                style={{ color: 'var(--success-color)' }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div
                                                                className='tx-status-indicator'
                                                                id={`error-${transaction.hash}-${index}`}
                                                            >
                                                                <i
                                                                    className='fas fa-times'
                                                                    style={{ color: 'var(--fail-color)' }}
                                                                />
                                                            </div>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`error-${transaction.hash}-${index}`}
                                                            >
                                                                Error in Txn: {transaction.error}
                                                            </UncontrolledTooltip>
                                                        </>
                                                    )}
                                                    <span
                                                        id={`tx-hash-${transaction.hash}-${index}`}
                                                        className={`${network.name}-text-color cursor-pointer`}
                                                        onClick={() =>
                                                            history.push(`/${network.name}/tx/${transaction.hash}`)
                                                        }
                                                    >
                                                        {formatBytes(transaction.hash, 12, 9, 0)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`tx-hash-${transaction.hash}-${index}`}
                                                    >
                                                        {transaction.hash}
                                                    </UncontrolledTooltip>
                                                    &nbsp;
                                                    <CopyButton
                                                        id={`copy-tx-hash-${transaction.hash}-${index}`}
                                                        data={transaction.hash}
                                                        text={'Copy Transaction Hash'}
                                                    />
                                                </td>
                                                <td>{transaction.traceType}</td>
                                                <td>
                                                    <span
                                                        id={`method-id-${transaction.hash}-${index}`}
                                                        className='method-id'
                                                    >
                                                        {transaction.functionName}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`method-id-${transaction.hash}-${index}`}
                                                    >
                                                        {transaction.functionName}
                                                    </UncontrolledTooltip>
                                                </td>
                                                <td>
                                                    {transaction.from.type === 'SCA' && (
                                                        <>
                                                            <i
                                                                className='fas fa-file-alt'
                                                                style={{
                                                                    fontSize: '14px',
                                                                    color: 'var(--grey-color)'
                                                                }}
                                                            />
                                                            &nbsp;
                                                        </>
                                                    )}
                                                    <span
                                                        id={`from-${transaction.hash}-${index}`}
                                                        className={`${
                                                            addressDisabled !== transaction.from.address &&
                                                            `${network.name}-text-color cursor-pointer`
                                                        }`}
                                                        onClick={() =>
                                                            history.push(
                                                                `/${network.name}/address/${transaction.from.address}`
                                                            )
                                                        }
                                                    >
                                                        {formatBytes(transaction.from.address, 20, 7, 6)}
                                                    </span>
                                                    <UncontrolledTooltip
                                                        placement='top'
                                                        target={`from-${transaction.hash}-${index}`}
                                                    >
                                                        {transaction.from.address}
                                                    </UncontrolledTooltip>
                                                    &nbsp;
                                                    <CopyButton
                                                        id={`copy-from-${transaction.hash}-${index}`}
                                                        data={transaction.from.address}
                                                        text={'Copy Address'}
                                                    />
                                                </td>
                                                <td>
                                                    {transaction.contractAddress ? (
                                                        <>
                                                            <span
                                                                id={`to-${transaction.hash}-${index}`}
                                                                className={`${network.name}-text-color cursor-pointer`}
                                                                onClick={() =>
                                                                    history.push(
                                                                        `/${network.name}/address/${transaction.contractAddress}`
                                                                    )
                                                                }
                                                            >
                                                                Create: &nbsp;
                                                                {formatBytes(transaction.contractAddress, 20, 7, 6)}
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`to-${transaction.hash}-${index}`}
                                                            >
                                                                {`New contract ${transaction.contractAddress}`}
                                                            </UncontrolledTooltip>
                                                            &nbsp;
                                                            <CopyButton
                                                                id={`copy-to-${transaction.hash}-${index}`}
                                                                className={`${
                                                                    addressDisabled !== transaction.contractAddress &&
                                                                    `${network.name}-text-color cursor-pointer`
                                                                }`}
                                                                data={transaction.contractAddress}
                                                                text={'Copy Address'}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            {transaction.to.type === 'SCA' && (
                                                                <>
                                                                    <i
                                                                        className='fas fa-file-alt'
                                                                        style={{
                                                                            fontSize: '14px',
                                                                            color: 'var(--grey-color)'
                                                                        }}
                                                                    />
                                                                    &nbsp;
                                                                </>
                                                            )}
                                                            <span
                                                                id={`to-${transaction.hash}-${index}`}
                                                                className={`${
                                                                    addressDisabled !== transaction.to.address &&
                                                                    `${network.name}-text-color cursor-pointer`
                                                                }`}
                                                                onClick={() =>
                                                                    history.push(
                                                                        `/${network.name}/address/${transaction.to.address}`
                                                                    )
                                                                }
                                                            >
                                                                {formatBytes(transaction.to.address, 20, 7, 6)}
                                                            </span>
                                                            <UncontrolledTooltip
                                                                placement='top'
                                                                target={`to-${transaction.hash}-${index}`}
                                                            >
                                                                {transaction.to.address}
                                                            </UncontrolledTooltip>
                                                            &nbsp;
                                                            <CopyButton
                                                                id={`copy-to-${transaction.hash}-${index}`}
                                                                data={transaction.to.address}
                                                                text={'Copy Address'}
                                                            />
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    {(transaction.value / 10 ** 18).toLocaleString(undefined, {
                                                        minimumFractionDigits: 0,
                                                        maximumFractionDigits: 18
                                                    })}
                                                    &nbsp;
                                                    {network.data.token}
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </>
                            )
                        })}
                    </>
                )}
            </tbody>
        </Table>
    )
}

module.exports = { InternalTransactionsTable }
